import { useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Edit, Loader2, Truck, Package, MapPin, Calendar, Hash, FileText, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DateCell } from "@/components/DateCell";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

interface DeliveryLine {
  id: string;
  lineNumber: number;
  description: string | null;
  quantityExpected: string;
  quantityPicked: string | null;
  quantityDelivered: string | null;
  uom: string | null;
  lotNumber: string | null;
  status: string;
  product?: { id: string; name: string };
}

interface DeliveryDetail {
  id: string;
  deliveryNumber: string;
  orderId: string | null;
  status: string;
  scheduledDate: string | null;
  actualDeliveryTime: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  podSignature: string | null;
  podImageUrl: string | null;
  routeId: string | null;
  stopSequence: number | null;
  notes: string | null;
  order?: {
    id: string; orderNumber: string; status: string;
    accountId: string | null; accountName: string | null;
  };
  shipment?: { id: string; shipmentNumber: string };
  warehouse?: { id: string; name: string };
  lines?: DeliveryLine[];
}

const DELIVERY_STATUSES = [
  { value: "PLANNED", label: "Planned" },
  { value: "PICKING", label: "Picking" },
  { value: "LOADED", label: "Loaded" },
  { value: "IN_TRANSIT", label: "In Transit" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "EXCEPTION", label: "Exception" },
];

function statusDotColor(status: string): string {
  switch (status) {
    case "PLANNED": return "bg-gray-400";
    case "PICKING": return "bg-yellow-500";
    case "LOADED": return "bg-blue-500";
    case "IN_TRANSIT": return "bg-indigo-500";
    case "DELIVERED": return "bg-green-500";
    case "EXCEPTION": return "bg-red-500";
    default: return "bg-gray-400";
  }
}

function formatStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

export default function DeliveryDetailPage() {
  const params = useParams<{ id: string }>();
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    status: "PLANNED",
    carrier: "",
    trackingNumber: "",
    notes: "",
  });

  const { data: delivery, isLoading, error } = useQuery<DeliveryDetail>({
    queryKey: ["/api/deliveries", params.id],
    queryFn: () => api.get<DeliveryDetail>(`/deliveries/${params.id}`, sessionId),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.patch(`/deliveries/${params.id}`, {
        status: editForm.status,
        carrier: editForm.carrier || null,
        trackingNumber: editForm.trackingNumber || null,
        notes: editForm.notes || null,
      }, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/deliveries", params.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/deliveries"] });
      toast({ title: "Delivery updated" });
      setEditOpen(false);
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleEditOpen = () => {
    if (delivery) {
      setEditForm({
        status: delivery.status || "PLANNED",
        carrier: delivery.carrier || "",
        trackingNumber: delivery.trackingNumber || "",
        notes: delivery.notes || "",
      });
    }
    setEditOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !delivery) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Delivery not found</p>
        <Button variant="outline" onClick={() => setLocation("/orders")}>
          Back to Orders
        </Button>
      </div>
    );
  }

  const lines = delivery.lines || [];

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  delivery.orderId
                    ? setLocation(`/orders/${delivery.orderId}`)
                    : setLocation("/orders")
                }
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight">
                {delivery.deliveryNumber}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {delivery.order?.accountName && delivery.order.accountId ? (
                <Link
                  href={`/accounts/${delivery.order.accountId}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {delivery.order.accountName}
                </Link>
              ) : delivery.order?.accountName ? (
                <span className="text-blue-600 dark:text-blue-400">
                  {delivery.order.accountName}
                </span>
              ) : null}
              {delivery.order?.accountName && (
                <span className="text-muted-foreground font-normal">/</span>
              )}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span>{formatStatusLabel(delivery.status)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleEditOpen}>
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor(delivery.status))} />
              <span className="text-sm font-medium">{delivery.status.replace(/_/g, " ")}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Scheduled</span>
              <p className="text-sm font-medium">
                <DateCell value={delivery.scheduledDate} />
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Delivered</span>
              <p className="text-sm font-medium">
                <DateCell value={delivery.actualDeliveryTime} />
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Carrier</span>
              <p className="text-sm font-medium">{delivery.carrier || "—"}</p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Tracking</span>
              <p className="text-sm font-medium">{delivery.trackingNumber || "—"}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {delivery.warehouse && (
          <div className="border-l-[3px] border-blue-500 bg-card rounded-r-lg shadow-sm">
            <div className="px-4 py-3">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <MapPin className="h-4 w-4 text-blue-500" />
                Warehouse
              </h2>
            </div>
            <div className="px-4 pb-4">
              <p className="text-sm font-medium">{delivery.warehouse.name}</p>
            </div>
          </div>
        )}
        {delivery.routeId && (
          <div className="border-l-[3px] border-orange-500 bg-card rounded-r-lg shadow-sm">
            <div className="px-4 py-3">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Truck className="h-4 w-4 text-orange-500" />
                Route
              </h2>
            </div>
            <div className="px-4 pb-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Route ID</span>
                <span className="text-sm font-medium">{delivery.routeId}</span>
              </div>
              {delivery.stopSequence != null && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Stop #</span>
                  <span className="text-sm font-medium">{delivery.stopSequence}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {delivery.podSignature && (
        <div className="border-l-[3px] border-green-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-green-500" />
              Proof of Delivery
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Signature</span>
              <span className="text-sm font-medium">{delivery.podSignature}</span>
            </div>
            {delivery.podImageUrl && (
              <div>
                <span className="text-sm text-muted-foreground">POD Image</span>
                <img src={delivery.podImageUrl} alt="POD" className="mt-1 max-h-48 rounded border" />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="border-l-[3px] border-emerald-500 bg-card rounded-r-lg shadow-sm">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 text-emerald-500" />
            Line Items
          </h2>
        </div>
        <div className="px-4 pb-4">
          {lines.length > 0 ? (
            <div className="rounded-md border overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Expected</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Picked</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Delivered</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">UOM</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Lot</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lines.map((line, idx) => (
                    <tr key={line.id}>
                      <td className="px-4 py-2 text-sm font-medium">{idx + 1}</td>
                      <td className="px-4 py-2 text-sm font-medium">
                        {line.description || line.product?.name || "—"}
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-medium">{line.quantityExpected}</td>
                      <td className="px-4 py-2 text-sm text-right">{line.quantityPicked ?? 0}</td>
                      <td className="px-4 py-2 text-sm text-right">{line.quantityDelivered ?? 0}</td>
                      <td className="px-4 py-2 text-sm">{line.uom || "EA"}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{line.lotNumber || "—"}</td>
                      <td className="px-4 py-2">
                        <Badge
                          variant={line.status === "delivered" ? "default" : "secondary"}
                          className="text-xs capitalize"
                        >
                          {line.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
              <Package className="h-10 w-10 mb-2 opacity-50" />
              <p>No line items</p>
            </div>
          )}
        </div>
      </div>

      {delivery.notes && (
        <div className="border-l-[3px] border-gray-400 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Notes
            </h2>
          </div>
          <div className="px-4 pb-4">
            <p className="text-sm whitespace-pre-wrap">{delivery.notes}</p>
          </div>
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Delivery</DialogTitle>
            <DialogDescription>Update the delivery information below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Status *</label>
              <Select
                value={editForm.status}
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DELIVERY_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Carrier</label>
              <Input
                value={editForm.carrier}
                onChange={(e) => setEditForm({ ...editForm, carrier: e.target.value })}
                placeholder="Carrier name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Tracking Number</label>
              <Input
                value={editForm.trackingNumber}
                onChange={(e) => setEditForm({ ...editForm, trackingNumber: e.target.value })}
                placeholder="Tracking number"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
                placeholder="Delivery notes..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
